import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IoSend, IoCheckmark, IoCheckmarkDone } from 'react-icons/io5';
import moment from 'moment';
import {
  getChatContacts,
  getChatMessages,
  sendChatMessage,
} from '../../../../redux/actions/Chatbox.action';

export default function Chat() {
  const dispatch = useDispatch();
  const chatbox = useSelector((state) => state.chatbox);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messageText, setMessageText] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => {
    dispatch(getChatContacts({}));
  }, []);

  useEffect(() => {
    if (selectedContact?.phoneNumber) {
      dispatch(getChatMessages(selectedContact.phoneNumber));
      const interval = setInterval(() => {
        dispatch(getChatMessages(selectedContact.phoneNumber));
      }, 8000);
      return () => clearInterval(interval);
    }
  }, [selectedContact]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatbox?.getChatMessages]);

  const contactList = chatbox?.chatContacts?.data?.data || [];
  const messages = chatbox?.getChatMessages?.data?.messages || [];

  const handleSend = () => {
    if (!messageText.trim() || !selectedContact) return;
    dispatch(
      sendChatMessage({ to: selectedContact.phoneNumber, text: messageText }),
    ).then(() => {
      setMessageText('');
      dispatch(getChatMessages(selectedContact.phoneNumber));
    });
  };

  const getInitials = (name) => (name ? name.charAt(0).toUpperCase() : '?');

  return (
    <div className="mt-[100px] mb-4">
      <div
        className="d-flex bg-white rounded shadow-sm"
        style={{ border: '1px solid #eee', height: '75vh' }}
      >
        {/* Left contact list */}
        <div
          style={{
            width: '320px',
            borderRight: '1px solid #eee',
            overflowY: 'auto',
          }}
        >
          <div className="p-3 fw-bold border-bottom">
            Chats ({contactList.length})
          </div>
          {contactList.map((c) => (
            <div
              key={c._id}
              onClick={() => setSelectedContact(c)}
              className={`d-flex align-items-center gap-3 p-3 cursor-pointer ${
                selectedContact?._id === c._id ? 'bg-light' : ''
              }`}
              style={{ borderBottom: '1px solid #f5f5f5', cursor: 'pointer' }}
            >
              <div
                className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                style={{ width: 40, height: 40, background: '#3b82f6' }}
              >
                {getInitials(c.fname)}
              </div>
              <div className="flex-grow-1">
                <div className="fw-bold">
                  {c.fname} {c.lname}
                </div>
                <div className="text-muted small">{c.phoneNumber}</div>
              </div>
              <div className="text-muted small">
                {c.lastMessageTime
                  ? moment(c.lastMessageTime).format('M/D/YYYY')
                  : ''}
              </div>
            </div>
          ))}
        </div>

        {/* Right chat window */}
        <div className="flex-grow-1 d-flex flex-column">
          {selectedContact ? (
            <>
              <div className="p-3 border-bottom d-flex align-items-center gap-3">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                  style={{ width: 40, height: 40, background: '#3b82f6' }}
                >
                  {getInitials(selectedContact.fname)}
                </div>
                <div>
                  <div className="fw-bold">
                    {selectedContact.fname} {selectedContact.lname}
                  </div>
                  <div className="text-muted small">
                    {selectedContact.phoneNumber}
                  </div>
                </div>
              </div>

              <div className="flex-grow-1 p-3" style={{ overflowY: 'auto' }}>
                {messages.length > 0 ? (
                  messages.map((msg) => (
                    <div
                      key={msg._id}
                      className={`d-flex mb-3 ${
                        msg.direction === 'outbound'
                          ? 'justify-content-end'
                          : 'justify-content-start'
                      }`}
                    >
                      <div
                        className="p-2 px-3 rounded"
                        style={{
                          maxWidth: '60%',
                          background:
                            msg.direction === 'outbound'
                              ? '#d9f2ff'
                              : '#f1f1f1',
                        }}
                      >
                        <div>{msg.text}</div>
                        <div className="text-muted small text-end mt-1 d-flex align-items-center justify-content-end gap-1">
                          <span>{moment(msg.timestamp).format('hh:mm A')}</span>
                          {msg.direction === 'outbound' && (
                            <>
                              {msg.status === 'read' ? (
                                <IoCheckmarkDone style={{ color: '#34b7f1' }} />
                              ) : msg.status === 'delivered' ? (
                                <IoCheckmarkDone className="text-muted" />
                              ) : (
                                <IoCheckmark className="text-muted" />
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-muted mt-5">
                    No messages yet
                  </div>
                )}
                <div ref={bottomRef} />
              </div>

              <div className="p-3 border-top d-flex gap-2">
                <input
                  type="text"
                  className="form-control shadow-none"
                  placeholder="Type a message..."
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                />
                <button
                  onClick={handleSend}
                  className="!bg-theme-dark text-white px-4 rounded border-0"
                >
                  <IoSend />
                </button>
              </div>
              <div className="text-center text-muted small py-2 border-top">
                Your free 24-hour window is over.{' '}
                <span className="text-success fw-bold">
                  WhatsApp via your own personal number.
                </span>
              </div>
            </>
          ) : (
            <div className="d-flex align-items-center justify-content-center h-100 text-muted">
              Select a contact to start chatting
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
